import { baseApi } from './baseApi';
import {
  KitchenOrderDto,
  KitchenOrdersResponse,
  CreateKitchenOrderDto,
  CreateKitchenOrdersResponse,
  UpdateKitchenOrderStatusDto,
  KitchenStatsDto,
  KitchenOrderStatus,
  KitchenNoteDto,
  CreateKitchenNoteDto,
  UpdateKitchenNoteDto,
  OrderItemWithoutComandaDto,
} from '../types/kitchen';

export const kitchenApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all kitchen orders with optional filters
    getKitchenOrders: builder.query<KitchenOrdersResponse, {
      areaFilter?: string;
      productionCenterIds?: string[];
      includeCompleted?: boolean;
      includeUncategorized?: boolean;
      completedSince?: string;
      orderByCompletedAtDesc?: boolean;
    } | void>({
      query: (params) => {
        // Build query string manually to handle array params correctly
        const searchParams = new URLSearchParams();
        if (params?.areaFilter) {
          searchParams.append('areaFilter', params.areaFilter);
        }
        if (params?.productionCenterIds?.length) {
          // ASP.NET Core accepts repeated query params for List<Guid>
          params.productionCenterIds.forEach(id => {
            searchParams.append('productionCenterIds', id);
          });
        }
        if (params?.includeCompleted) {
          searchParams.append('includeCompleted', 'true');
        }
        if (params?.includeUncategorized) {
          searchParams.append('includeUncategorized', 'true');
        }
        if (params?.completedSince) {
          searchParams.append('completedSince', params.completedSince);
        }
        if (params?.orderByCompletedAtDesc) {
          searchParams.append('orderByCompletedAtDesc', 'true');
        }
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/kitchen/orders?${queryString}` : '/kitchen/orders',
        };
      },
      providesTags: (result) => [
        { type: 'KitchenOrder' as const, id: 'LIST' },
        ...(result?.orders || []).map(({ id }) => ({ type: 'KitchenOrder' as const, id })),
      ],
    }),

    // Get a single kitchen order by ID
    getKitchenOrder: builder.query<KitchenOrderDto, string>({
      query: (id) => `/kitchen/orders/${id}`,
      providesTags: (result, error, id) => [
        { type: 'KitchenOrder' as const, id },
      ],
    }),

    // Create a new kitchen order (when sending comanda).
    // Response is a wrapper with an `orders` array because a single comanda can split into
    // 2 KitchenOrders when it mixes daily-menu items with regular items.
    createKitchenOrder: builder.mutation<CreateKitchenOrdersResponse, CreateKitchenOrderDto>({
      query: (data) => ({
        url: '/kitchen/orders',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'KitchenOrder' as const, id: 'LIST' },
      ],
    }),

    // Update kitchen order status
    updateKitchenOrderStatus: builder.mutation<KitchenOrderDto, {
      id: string;
      data: UpdateKitchenOrderStatusDto;
    }>({
      query: ({ id, data }) => ({
        url: `/kitchen/orders/${id}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'KitchenOrder' as const, id },
        { type: 'KitchenOrder' as const, id: 'LIST' },
      ],
    }),

    // Quick action: Mark order as Ready
    markKitchenOrderReady: builder.mutation<KitchenOrderDto, string>({
      query: (id) => ({
        url: `/kitchen/orders/${id}/ready`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'KitchenOrder' as const, id },
        { type: 'KitchenOrder' as const, id: 'LIST' },
        { type: 'KitchenOrder' as const, id: 'STATS' },
      ],
    }),

    // Quick action: Mark order as Completed
    markKitchenOrderCompleted: builder.mutation<KitchenOrderDto, string>({
      query: (id) => ({
        url: `/kitchen/orders/${id}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'KitchenOrder' as const, id },
        { type: 'KitchenOrder' as const, id: 'LIST' },
        { type: 'KitchenOrder' as const, id: 'STATS' },
      ],
    }),

    // Complete specific items in a kitchen order (partial dispatch by production center)
    completeKitchenOrderItems: builder.mutation<KitchenOrderDto, { id: string; kitchenOrderItemIds: string[] }>({
      query: ({ id, kitchenOrderItemIds }) => ({
        url: `/kitchen/orders/${id}/complete-items`,
        method: 'POST',
        body: { kitchenOrderItemIds },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'KitchenOrder' as const, id },
        { type: 'KitchenOrder' as const, id: 'LIST' },
        { type: 'KitchenOrder' as const, id: 'STATS' },
      ],
    }),

    // Update status of specific items in a kitchen order (per-center status)
    updateKitchenOrderItemsStatus: builder.mutation<KitchenOrderDto, { id: string; kitchenOrderItemIds: string[]; status: KitchenOrderStatus }>({
      query: ({ id, kitchenOrderItemIds, status }) => ({
        url: `/kitchen/orders/${id}/update-items-status`,
        method: 'POST',
        body: { kitchenOrderItemIds, status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'KitchenOrder' as const, id },
        { type: 'KitchenOrder' as const, id: 'LIST' },
        { type: 'KitchenOrder' as const, id: 'STATS' },
      ],
    }),

    // Get kitchen statistics
    getKitchenStats: builder.query<KitchenStatsDto, void>({
      query: () => '/kitchen/stats',
      providesTags: [{ type: 'KitchenOrder' as const, id: 'STATS' }],
    }),

    // Get available areas for filtering
    getKitchenAreas: builder.query<string[], void>({
      query: () => '/kitchen/areas',
      providesTags: [{ type: 'KitchenOrder' as const, id: 'AREAS' }],
    }),

    // Check if order has an active (non-completed) kitchen order
    hasActiveKitchenOrder: builder.query<{ hasActiveOrder: boolean }, string>({
      query: (orderId) => `/kitchen/orders/order/${orderId}/active`,
      providesTags: [{ type: 'KitchenOrder' as const, id: 'LIST' }],
    }),

    // Get all kitchen orders for a specific order (for resend selection)
    getKitchenOrdersByOrderId: builder.query<KitchenOrderDto[], string>({
      query: (orderId) => `/kitchen/orders/by-order/${orderId}`,
      providesTags: [{ type: 'KitchenOrder' as const, id: 'LIST' }],
    }),

    // Renglones vivos que ninguna comanda cubre. Debería venir siempre vacío ahora que la
    // comanda se crea junto con los renglones; alimenta el rescate del modal de reenvío.
    getOrderItemsWithoutComanda: builder.query<OrderItemWithoutComandaDto[], string>({
      query: (orderId) => `/kitchen/orders/by-order/${orderId}/missing`,
      providesTags: [{ type: 'KitchenOrder' as const, id: 'LIST' }],
    }),

    // Manda a cocina renglones que se quedaron sin comanda. Idempotente: reintentar no duplica.
    comandearOrderItems: builder.mutation<
      CreateKitchenOrdersResponse,
      { orderId: string; orderItemIds: string[] }
    >({
      query: ({ orderId, orderItemIds }) => ({
        url: `/kitchen/orders/by-order/${orderId}/comandear`,
        method: 'POST',
        body: { orderItemIds },
      }),
      invalidatesTags: [{ type: 'KitchenOrder' as const, id: 'LIST' }],
    }),

    // ==================== KITCHEN NOTES ====================

    // Get kitchen notes (active by default, or all with includeInactive=true)
    getKitchenNotes: builder.query<KitchenNoteDto[], { includeInactive?: boolean } | void>({
      query: (params) => ({
        url: '/kitchen/notes',
        params: params ? { includeInactive: params.includeInactive } : undefined,
      }),
      providesTags: [{ type: 'KitchenOrder' as const, id: 'NOTES' }],
    }),

    // Create a new kitchen note
    createKitchenNote: builder.mutation<KitchenNoteDto, CreateKitchenNoteDto>({
      query: (data) => ({
        url: '/kitchen/notes',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'KitchenOrder' as const, id: 'NOTES' }],
    }),

    // Update a kitchen note
    updateKitchenNote: builder.mutation<KitchenNoteDto, { id: string; data: UpdateKitchenNoteDto }>({
      query: ({ id, data }) => ({
        url: `/kitchen/notes/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'KitchenOrder' as const, id: 'NOTES' }],
    }),

    // Delete a kitchen note
    deleteKitchenNote: builder.mutation<void, string>({
      query: (id) => ({
        url: `/kitchen/notes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'KitchenOrder' as const, id: 'NOTES' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetKitchenOrdersQuery,
  useGetKitchenOrderQuery,
  useCreateKitchenOrderMutation,
  useUpdateKitchenOrderStatusMutation,
  useMarkKitchenOrderReadyMutation,
  useMarkKitchenOrderCompletedMutation,
  useCompleteKitchenOrderItemsMutation,
  useUpdateKitchenOrderItemsStatusMutation,
  useGetKitchenStatsQuery,
  useGetKitchenAreasQuery,
  useLazyHasActiveKitchenOrderQuery,
  useLazyGetKitchenOrdersByOrderIdQuery,
  useLazyGetOrderItemsWithoutComandaQuery,
  useComandearOrderItemsMutation,
  // Kitchen Notes
  useGetKitchenNotesQuery,
  useCreateKitchenNoteMutation,
  useUpdateKitchenNoteMutation,
  useDeleteKitchenNoteMutation,
} = kitchenApi;
